/**
 * lib/format — money / percent / time formatters shared across the terminal so every
 * figure reads consistently (tabular numerals, signed deltas, compact times).
 */
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function money(n: number, decimals: 0 | 2 = 2): string {
  return (decimals === 0 ? usd0 : usd2).format(n);
}

/** Signed money, e.g. +$208.80 / −$27.60 (uses a real minus glyph). */
export function signedMoney(n: number): string {
  const s = usd2.format(Math.abs(n));
  return n < 0 ? `−${s}` : `+${s}`;
}

/** Fraction → signed percent, e.g. 0.0142 → +1.42%. */
export function pct(fraction: number, decimals = 2): string {
  const v = (fraction * 100).toFixed(decimals);
  const sign = fraction > 0 ? '+' : fraction < 0 ? '−' : '';
  return `${sign}${Math.abs(Number(v)).toFixed(decimals)}%`;
}

/** Plain (unsigned) percent from a fraction. */
export function pctPlain(fraction: number, decimals = 1): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

export function num(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Compact "12m ago" / "3h ago" relative time. */
export function ago(epochMs: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - epochMs) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function directionClass(n: number): 'profit' | 'loss' | '' {
  return n > 0 ? 'profit' : n < 0 ? 'loss' : '';
}
